import {
  Box, IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTimingTab } from '../../../contexts/OverviewContext';
import { useTranslation } from 'react-i18next';
import { CaretRightIcon, CheckIcon } from '../icons';
import DrawerBottom from '../DrawerBottom';

// Styled container for the period selector
const PeriodSelectorContainer = styled(Box)(({ theme }) => ({
  minHeight: 40,
  borderRadius: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.common.white,
}));

export interface PeriodSelectorProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  showCurrentLabel?: boolean;
  timingType?: 'weekly' | 'monthly' | 'yearly';
  onTimingTypeChange?: (type: 'monthly' | 'yearly') => void;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  currentDate,
  onDateChange,
  showCurrentLabel = true,
  timingType = 'monthly',
  onTimingTypeChange,
}) => {
  const { t } = useTranslation(['component']);
  const theme = useTheme();
  const [openTypePicker, setOpenTypePicker] = useState(false);
  const [timingTab, setTimingTab] = useTimingTab();

  const isCurrentMonth = () => {
    const now = new Date();
    return currentDate.getMonth() === now.getMonth() &&
      currentDate.getFullYear() === now.getFullYear();
  };

  const isPreviousMonth = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
    return currentDate.getMonth() === lastMonth.getMonth() &&
      currentDate.getFullYear() === lastMonth.getFullYear();
  };

  const isNextMonth = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1);
    return currentDate.getMonth() === nextMonth.getMonth() &&
      currentDate.getFullYear() === nextMonth.getFullYear();
  };

  const isCurrentWeek = () => {
    const now = new Date();
    // Get start of current week (Monday)
    const currentWeekStart = new Date(now);
    const dayOfWeek = currentWeekStart.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    currentWeekStart.setDate(currentWeekStart.getDate() - daysToMonday);
    currentWeekStart.setHours(0, 0, 0, 0);

    // Get start of selected week (Monday)
    const selectedWeekStart = new Date(currentDate);
    const selectedDayOfWeek = selectedWeekStart.getDay();
    const selectedDaysToMonday = selectedDayOfWeek === 0 ? 6 : selectedDayOfWeek - 1;
    selectedWeekStart.setDate(selectedWeekStart.getDate() - selectedDaysToMonday);
    selectedWeekStart.setHours(0, 0, 0, 0);

    return currentWeekStart.getTime() === selectedWeekStart.getTime();
  };

  const isPreviousWeek = () => {
    const now = new Date();
    // Get start of last week (Monday)
    const lastWeekStart = new Date(now);
    const dayOfWeek = lastWeekStart.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    lastWeekStart.setDate(lastWeekStart.getDate() - daysToMonday - 7);
    lastWeekStart.setHours(0, 0, 0, 0);

    // Get start of selected week (Monday)
    const selectedWeekStart = new Date(currentDate);
    const selectedDayOfWeek = selectedWeekStart.getDay();
    const selectedDaysToMonday = selectedDayOfWeek === 0 ? 6 : selectedDayOfWeek - 1;
    selectedWeekStart.setDate(selectedWeekStart.getDate() - selectedDaysToMonday);
    selectedWeekStart.setHours(0, 0, 0, 0);

    return lastWeekStart.getTime() === selectedWeekStart.getTime();
  };

  const isNextWeek = () => {
    const now = new Date();
    // Get start of next week (Monday)
    const nextWeekStart = new Date(now);
    const dayOfWeek = nextWeekStart.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    nextWeekStart.setDate(nextWeekStart.getDate() - daysToMonday + 7);
    nextWeekStart.setHours(0, 0, 0, 0);

    // Get start of selected week (Monday)
    const selectedWeekStart = new Date(currentDate);
    const selectedDayOfWeek = selectedWeekStart.getDay();
    const selectedDaysToMonday = selectedDayOfWeek === 0 ? 6 : selectedDayOfWeek - 1;
    selectedWeekStart.setDate(selectedWeekStart.getDate() - selectedDaysToMonday);
    selectedWeekStart.setHours(0, 0, 0, 0);

    return nextWeekStart.getTime() === selectedWeekStart.getTime();
  };

  const formatMonthYear = (date: Date) => {
    if (timingTab === 'yearly') {
      return date.getFullYear().toString();
    }

    if (timingTab === 'weekly') {
      // Calculate week start (Monday) and end (Sunday) first for week number calculation
      const weekStart = new Date(date);
      const dayOfWeek = weekStart.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      weekStart.setDate(weekStart.getDate() - daysToMonday);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      // Calculate week number (ISO week)
      const getWeekNumber = (date: Date): number => {
        const tempDate = new Date(date.getTime());
        tempDate.setHours(0, 0, 0, 0);
        // Thursday in current week decides the year
        tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
        // January 4 is always in week 1
        const week1 = new Date(tempDate.getFullYear(), 0, 4);
        // Adjust to Thursday in week 1 and count weeks from there
        return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
      };

      const weekNumber = getWeekNumber(weekStart);

      if (showCurrentLabel && isCurrentWeek()) {
        return `${t('monthSelector.thisWeek')} (${t('monthSelector.week')} ${weekNumber})`;
      }

      if (showCurrentLabel && isPreviousWeek()) {
        return `${t('monthSelector.lastWeek')} (${t('monthSelector.week')} ${weekNumber})`;
      }

      if (showCurrentLabel && isNextWeek()) {
        return `${t('monthSelector.nextWeek')} (${t('monthSelector.week')} ${weekNumber})`;
      }

      const monthKeys = [
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'
      ];

      const startMonth = t(`monthSelector.months.${monthKeys[weekStart.getMonth()]}`);
      const endMonth = t(`monthSelector.months.${monthKeys[weekEnd.getMonth()]}`);

      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${t('monthSelector.week')} ${weekNumber}: ${startMonth} ${weekStart.getDate()}-${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
      } else {
        return `${t('monthSelector.week')} ${weekNumber}: ${startMonth} ${weekStart.getDate()} - ${endMonth} ${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
      }
    }

    // Monthly format (default)
    if (showCurrentLabel && isCurrentMonth()) {
      return t('monthSelector.thisMonth');
    }

    if (showCurrentLabel && isPreviousMonth()) {
      return t('monthSelector.lastMonth');
    }

    if (showCurrentLabel && isNextMonth()) {
      return t('monthSelector.nextMonth');
    }

    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];

    const monthKey = monthKeys[date.getMonth()];
    const monthName = t(`monthSelector.months.${monthKey}`);
    const year = date.getFullYear();

    return `${monthName} ${year}`;
  };

  const goToPreviousPeriod = () => {
    const previousDate = new Date(currentDate);

    if (timingTab === 'yearly') {
      previousDate.setFullYear(previousDate.getFullYear() - 1);
    } else if (timingTab === 'weekly') {
      previousDate.setDate(previousDate.getDate() - 7);
    } else {
      // Monthly (default)
      previousDate.setMonth(previousDate.getMonth() - 1);
    }

    onDateChange(previousDate);
  };

  const goToNextPeriod = () => {
    const nextDate = new Date(currentDate);

    if (timingTab === 'yearly') {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    } else if (timingTab === 'weekly') {
      nextDate.setDate(nextDate.getDate() + 7);
    } else {
      // Monthly (default)
      nextDate.setMonth(nextDate.getMonth() + 1);
    }

    onDateChange(nextDate);
  };

  // Always place effects right above the return
  useEffect(() => {
    // Keep URL/state in sync if parent passes a different timingType
    if (timingType && timingType !== timingTab) {
      if (timingType === 'monthly' || timingType === 'yearly' || timingType === 'weekly') {
        setTimingTab(timingType);
      }
    }
  }, [timingType, timingTab, setTimingTab]);

  return (
    <PeriodSelectorContainer>
      <IconButton
        size="large"
        onClick={goToPreviousPeriod}
        aria-label={timingTab === 'yearly' ? 'Previous year' : timingTab === 'weekly' ? 'Previous week' : t('monthSelector.previousMonthAriaLabel')}
        sx={{
          color: theme.palette.secondary.main,
        }}
      >
        <CaretRightIcon style={{ fontSize: 26, transform: 'rotate(180deg)' }} />
      </IconButton>

      <Typography
        fontWeight={500}
        onClick={() => setOpenTypePicker(true)}
        sx={{
          flex: 1,
          textAlign: 'center',
          color: 'inherit',
          userSelect: 'none',
          cursor: 'pointer',
        }}
      >
        {formatMonthYear(currentDate)}
      </Typography>

      <IconButton
        size="large"
        onClick={goToNextPeriod}
        aria-label={timingTab === 'yearly' ? 'Next year' : timingTab === 'weekly' ? 'Next week' : t('monthSelector.nextMonthAriaLabel')}
        sx={{
          color: theme.palette.secondary.main,
        }}
      >
        <CaretRightIcon style={{ fontSize: 26 }} />
      </IconButton>

      <DrawerBottom
        height="auto"
        open={openTypePicker}
        onClose={() => setOpenTypePicker(false)}
        title={t('periodSelectorRange.title', { ns: 'component', defaultValue: 'Filter time' })}
      >
        <List sx={{ mx: -2 }}>
          {([
            { key: 'monthly', label: t('periodSelectorRange.byMonth', { ns: 'component', defaultValue: 'Theo tháng' }) },
            { key: 'yearly', label: t('periodSelectorRange.byYear', { ns: 'component', defaultValue: 'Theo năm' }) },
          ] as { key: 'monthly' | 'yearly'; label: string }[]).map((item) => {
            const isActive = (timingTab === item.key);
            return (
              <ListItemButton
                key={item.key}
                selected={isActive}
                onClick={() => {
                  setTimingTab(item.key);
                  onTimingTypeChange?.(item.key);
                  setOpenTypePicker(false);
                }}
                sx={(theme) => ({
                  borderRadius: 0,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.pink[500],
                    color: theme.palette.common.white,
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: theme.palette.pink[300],
                  },
                })}
              >
                <ListItemText primary={item.label} />
                {isActive && <ListItemIcon><CheckIcon style={{ fontSize: 24, color: theme.palette.common.white }} /></ListItemIcon>}
              </ListItemButton>
            );
          })}
        </List>
      </DrawerBottom>
    </PeriodSelectorContainer>
  );
};

export default PeriodSelector; 